import dayjs from 'dayjs';
import { Virtuoso } from 'react-virtuoso';
import { useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, ListItem, Typography, ListItemText } from '@mui/material';

import { usePaginatedApi } from 'src/hooks/use-paginated-api';
import { useSSRPaginatedInfiniteScrolling } from 'src/hooks/use-ssr-paginated-infinite-scrolling';

import { endpoints } from 'src/utils/axios';


export interface EntityAuditTrailProps {
  entityId: string | number;
  entityType: string;
  textVariant?: 'body2' | 'caption';
  endpoint?: string;
}

export function EntityAuditTrail({ entityId, entityType, textVariant = 'body2', endpoint = endpoints.auditTrails.get }: EntityAuditTrailProps) {
  const { t } = useTranslation('organization');
  
  const {
    data: auditTrailData,
    page,
    pageSize,
    onPageLoaded,
    onPageChange,
    total,
  } = useSSRPaginatedInfiniteScrolling(100);

  const { data: auditTrail, pagination: auditTrailPagination } = usePaginatedApi({
    endpoint: endpoint,
    paginationParams: {
      page: page+1,
      take: pageSize,
    },
    additionalParams: {
      entityFilters: JSON.stringify([[entityType, Number(entityId)]]),
    },
  });

  useEffect(() => {
    onPageLoaded(auditTrail, auditTrailPagination);
  }, [auditTrail, auditTrailPagination, onPageLoaded]);

  const loadMore = useCallback(() => {
    if (auditTrailData.length < total) {
      onPageChange(page + 1);
    }
  }, [auditTrailData.length, total, page, onPageChange]);

  const sortedData = useMemo(
    () => [...auditTrailData].sort((a, b) => dayjs.utc(a.createdAt).valueOf() - dayjs.utc(b.createdAt).valueOf()),
    [auditTrailData]
  );

  const renderAuditTrailItem = useCallback((index: number) => {
    const item = sortedData[index];
    if (!item) return null;

    return (
      <ListItem 
        sx={{ 
          px: 0,
          py: 0.5,
          minHeight: 'auto',
          alignItems: 'flex-start',
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1 }}>
                <Box sx={{ 
                  width: 4, 
                  height: 4, 
                  borderRadius: '50%', 
                  backgroundColor: 'text.secondary',
                  mr: 1.5,
                  mt: 0.8,
                  flexShrink: 0
                }} />
                <Typography variant={textVariant} color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {item.eventSummary}
                </Typography>
              </Box>
              <Typography variant={textVariant} color="text.secondary" sx={{ flexShrink: 0, ml: 2 }}>
                {dayjs.utc(item.createdAt).format('MMM D [at] HH:mm')}
              </Typography>
            </Box>
          }
        />
      </ListItem>
    );
  }, [sortedData, textVariant]);

  if (!auditTrailData || auditTrailData.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('audit-trails.noData')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', width: '100%', px: 1, bgcolor: 'background.neutral' }}>
      <Virtuoso
        data={sortedData}
        itemContent={renderAuditTrailItem}
        endReached={loadMore}
        overscan={200}
        components={{
          Footer: () => (
            sortedData.length < total ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('audit-trails.loadingMore')}
                </Typography>
              </Box>
            ) : null
          ),
        }}
      />
    </Box>
  );
}
